'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import I18n from 'I18n';
import PropTypes from 'prop-types';
import { useContext, useMemo, useRef, useState, useEffect, memo } from 'react';
import usePrevious from 'react-utils/hooks/usePrevious';
import useUniqueId from 'react-utils/hooks/useUniqueId';
import invariant from 'react-utils/invariant';
import styled from 'styled-components';
import cx from 'classnames';
import { FieldsetContext } from '../context/FieldsetContext';
import { compare } from '../core/SimpleDate';
import { SimpleDateRangeRecord } from '../core/SimpleDateRange';
import SyntheticEvent from '../core/SyntheticEvent';
import Controllable from '../decorators/Controllable';
import ShareInput from '../decorators/ShareInput';
import { SimpleDateType } from '../types/SimpleDateTypes';
import { hidden } from '../utils/propTypes/decorators';
import { LAST_MONTH, LAST_QUARTER, LAST_THIRTY_DAYS, LAST_THREE_MONTHS, LAST_WEEK, LAST_YEAR, THIS_MONTH, THIS_QUARTER, THIS_WEEK, THIS_YEAR, TODAY, YESTERDAY } from './dateRangePresets';
import { getPageFromDate } from './internal/calendarUtils';
import DateRangePopover from './internal/DateRangePopover';
import UsesToday from './internal/UsesToday';
import UIDateInput from './UIDateInput';
const Wrapper = styled.div.withConfig({
  displayName: "UIDateRange__Wrapper",
  componentId: "ygj26u-0"
})(["display:flex;& > *{flex:1 1 0%;}"]);
const Separator = styled.div.withConfig({
  displayName: "UIDateRange__Separator",
  componentId: "ygj26u-1"
})(["flex-grow:0;align-self:center;flex-basis:auto;padding:0 10px;"]); // Ticket to add missing prop types: https://git.hubteam.com/HubSpot/UIComponents/issues/11739

const UIDateRange = /*#__PURE__*/memo(props => {
  const {
    // eslint-disable-next-line react/prop-types
    'aria-describedby': ariaDescribedBy,
    // eslint-disable-next-line react/prop-types
    'aria-invalid': ariaInvalid,
    // eslint-disable-next-line react/prop-types
    'aria-labelledby': ariaLabelledBy,
    // eslint-disable-next-line react/prop-types
    'aria-required': ariaRequired,
    calendarPage,
    clearLabel,
    DateInput,
    disabled,
    endInputRef: endInputRefProp,
    error,
    format,
    id,
    inputRef,
    min,
    minValidationMessage,
    max,
    maxValidationMessage,
    onCalendarPageChange,
    onChange,
    onOpenChange,
    open,
    presets,
    readOnly,
    required,
    showPresets,
    use,
    today,
    tz,
    value
  } = props,
        rest = _objectWithoutPropertiesLoose(props, ["aria-describedby", "aria-invalid", "aria-labelledby", "aria-required", "calendarPage", "clearLabel", "DateInput", "disabled", "endInputRef", "error", "format", "id", "inputRef", "min", "minValidationMessage", "max", "maxValidationMessage", "onCalendarPageChange", "onChange", "onOpenChange", "open", "presets", "readOnly", "required", "showPresets", "use", "today", "tz", "value"]);

  if (process.env.NODE_ENV !== 'production') {
    invariant(!(value instanceof SimpleDateRangeRecord), "UIDateRange's value uses plain objects instead of SimpleDateRange. Make sure you're not relying on SimpleDateRange methods when using values from the onChange handlers.");
  }

  const computedValue = useMemo(() => {
    const obj = {
      startDate: null,
      endDate: null
    };

    if (value) {
      const {
        presetId,
        startDate,
        endDate,
        getValue
      } = value;
      if (presetId) obj.presetId = presetId;
      if (startDate) obj.startDate = startDate;
      if (endDate) obj.endDate = endDate;
      if (getValue) Object.assign(obj, getValue(tz));
    }

    return obj;
  }, [value, tz]);
  const {
    startDate,
    endDate
  } = computedValue; // Values used for internal state

  const [startDateInputValue, setStartDateInputValue] = useState(startDate);
  const [endDateInputValue, setEndDateInputValue] = useState(endDate);
  const computedInternalValue = useMemo(() => Object.assign({}, computedValue, {
    startDate: startDateInputValue,
    endDate: endDateInputValue
  }), [computedValue, startDateInputValue, endDateInputValue]); // Values used in event handlers

  const startDateInputValueRef = useRef();
  startDateInputValueRef.current = startDateInputValue;
  const endDateInputValueRef = useRef();
  endDateInputValueRef.current = endDateInputValue; // Handle external value changes

  useEffect(() => {
    if (startDateInputValue !== startDate) {
      setStartDateInputValue(startDate);
    }

    if (endDateInputValue !== endDate) {
      setEndDateInputValue(endDate);
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [startDate, endDate]);
  const fieldsetContext = useContext(FieldsetContext);
  const computedDisabled = disabled || fieldsetContext.disabled;
  const inputProps = {
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    'aria-required': ariaRequired,
    disabled: computedDisabled,
    format,
    max,
    min,
    readOnly,
    required,
    use
  };
  const [activeBound, setActiveBound] = useState('start');
  const prevActiveBound = usePrevious(activeBound);
  const calendarId = useUniqueId(`uidaterange-`);
  const startLabelId = useUniqueId(`uidaterange-start-label-`);
  const endLabelId = useUniqueId(`uidaterange-end-label-`); // Ticket filed here to fix this issue: https://git.hubteam.com/HubSpot/UIComponents/issues/11740
  // eslint-disable-next-line react-hooks/rules-of-hooks

  const endInputRef = endInputRefProp || useRef(null);

  const handleKeyDown = evt => {
    const {
      key
    } = evt;

    if (key === 'Escape') {
      if (!open || !onOpenChange) return;
      evt.preventDefault();
      evt.stopPropagation();
      onOpenChange(SyntheticEvent(false));
    }
  };

  return /*#__PURE__*/_jsx(DateRangePopover // Edge case: If `open` and `activeBound` becomes null (e.g. when window loses focus), reset the activeBound
  , {
    activeBound: open && activeBound === null ? prevActiveBound : activeBound,
    calendarId: calendarId,
    calendarPage: calendarPage,
    clearLabel: clearLabel,
    max: max,
    maxValidationMessage: maxValidationMessage,
    min: min,
    minValidationMessage: minValidationMessage,
    onActiveBoundChange: evt => {
      (evt.target.value === 'end' ? endInputRef : inputRef).current.focus();
    },
    onCalendarPageChange: onCalendarPageChange,
    onChange: evt => {
      onChange(evt); // If the user just picked an end date, close the popover.

      const newValue = evt.target.value;
      if (newValue.endDate !== endDate) onOpenChange(SyntheticEvent(false));
    },
    onOpenChange: onOpenChange,
    open: open && !computedDisabled && !readOnly,
    presets: presets,
    showPresets: showPresets,
    today: today,
    tz: tz,
    value: computedInternalValue,
    onFocusLeave: () => // If focus leaves the popover and either input isn't active, close it
    activeBound === null && onOpenChange(SyntheticEvent(false)),
    children: /*#__PURE__*/_jsxs(Wrapper, Object.assign({}, rest, {
      children: [/*#__PURE__*/_jsx("span", {
        className: "sr-only",
        id: startLabelId,
        children: I18n.text('ui.dateRange.startDate')
      }), /*#__PURE__*/_jsx("span", {
        className: "sr-only",
        id: endLabelId,
        children: I18n.text('ui.dateRange.endDate')
      }), /*#__PURE__*/_jsx(DateInput, Object.assign({}, inputProps, {
        "aria-labelledby": cx(ariaLabelledBy, startLabelId),
        error: error || !!(startDateInputValue && endDate && compare(startDateInputValue, endDate) === 1),
        id: id
        /* support the htmlFor from UIFormControl */
        ,
        inputRef: inputRef,
        onChange: ({
          target: {
            value: newStartDate
          }
        }) => {
          setStartDateInputValue(newStartDate);

          if (!endDateInputValue || newStartDate === null || compare(newStartDate, endDateInputValue) !== 1) {
            // Make sure the calendar view shows the new selection.
            onCalendarPageChange(SyntheticEvent(getPageFromDate(newStartDate)));
            onChange(SyntheticEvent(Object.assign({}, computedValue, {
              startDate: newStartDate,
              endDate: endDateInputValueRef.current
            })));
          }
        },
        onFocus: () => {
          if (readOnly || computedDisabled) return;
          setActiveBound('start');
          onCalendarPageChange(SyntheticEvent(getPageFromDate(startDateInputValue)));
        },
        onBlur: () => {
          if (readOnly || computedDisabled) return;
          setActiveBound(null);
        },
        onOpenChange: onOpenChange,
        onKeyDown: handleKeyDown,
        open: false,
        originDate: endDate,
        tz: tz,
        value: startDateInputValue
      })), /*#__PURE__*/_jsx(Separator, {
        children: I18n.text('ui.dateRange.to')
      }), /*#__PURE__*/_jsx(DateInput, Object.assign({}, inputProps, {
        "aria-labelledby": cx(ariaLabelledBy, endLabelId),
        error: error || !!(startDate && endDateInputValue && compare(startDate, endDateInputValue) === 1),
        inputRef: endInputRef,
        onChange: ({
          target: {
            value: newEndDate
          }
        }) => {
          setEndDateInputValue(newEndDate);

          if (!startDateInputValue || newEndDate === null || compare(startDateInputValue, newEndDate) !== 1) {
            // Make sure the calendar view shows the new selection.
            onCalendarPageChange(SyntheticEvent(getPageFromDate(newEndDate)));
            onChange(SyntheticEvent(Object.assign({}, computedValue, {
              startDate: startDateInputValueRef.current,
              endDate: newEndDate
            })));
          }
        },
        onFocus: () => {
          if (readOnly || computedDisabled) return;
          setActiveBound('end');
          onCalendarPageChange(SyntheticEvent(getPageFromDate(endDateInputValue)));
        },
        onBlur: () => {
          if (readOnly || computedDisabled) return;
          setActiveBound(null);
        },
        onOpenChange: onOpenChange,
        onKeyDown: handleKeyDown,
        open: false,
        originDate: startDate,
        tz: tz,
        value: endDateInputValue
      }))]
    }))
  });
});
const dateRangeValueType = PropTypes.shape({
  startDate: SimpleDateType,
  endDate: SimpleDateType
});
const dateRangePresetType = PropTypes.shape({
  presetId: PropTypes.string.isRequired,
  getValue: PropTypes.func.isRequired,
  getText: PropTypes.func.isRequired
});
const valueOrPresetType = PropTypes.oneOfType([dateRangeValueType, dateRangePresetType]);
UIDateRange.propTypes = {
  calendarPage: UIDateInput.propTypes.calendarPage,
  clearLabel: UIDateInput.propTypes.clearLabel,
  disabled: PropTypes.bool,
  DateInput: hidden(PropTypes.elementType),
  endInputRef: hidden(UIDateInput.propTypes.inputRef),
  error: PropTypes.bool,
  format: UIDateInput.propTypes.format,
  inputRef: UIDateInput.propTypes.inputRef,
  max: UIDateInput.propTypes.max,
  maxValidationMessage: UIDateInput.propTypes.maxValidationMessage,
  min: UIDateInput.propTypes.min,
  minValidationMessage: UIDateInput.propTypes.minValidationMessage,
  onCalendarPageChange: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  presets: PropTypes.arrayOf(dateRangePresetType.isRequired).isRequired,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  showPresets: PropTypes.bool,
  tz: UIDateInput.propTypes.tz,
  use: UIDateInput.propTypes.use,
  today: SimpleDateType.isRequired,
  value: valueOrPresetType
};
UIDateRange.defaultProps = {
  clearLabel: UIDateInput.defaultProps.clearLabel,
  DateInput: UIDateInput,
  format: 'L',
  open: false,
  maxValidationMessage: UIDateInput.defaultProps.maxValidationMessage,
  minValidationMessage: UIDateInput.defaultProps.minValidationMessage,
  presets: [TODAY, YESTERDAY, THIS_WEEK, LAST_WEEK, THIS_MONTH, LAST_MONTH, LAST_THIRTY_DAYS, LAST_THREE_MONTHS, THIS_QUARTER, LAST_QUARTER, LAST_YEAR, THIS_YEAR],
  showPresets: true,
  value: {
    startDate: null,
    endDate: null
  }
};
UIDateRange.displayName = 'UIDateRange';
export default ShareInput(Controllable(UsesToday(UIDateRange), ['calendarPage', 'open', 'value']));